import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, isSubscribed } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { CheckCircle2, Lock, Loader2 } from "lucide-react";

export default function Subscribe() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const subscribed = isSubscribed(user);

  useEffect(() => {
    if (sp.get("canceled")) toast.info("Checkout canceled. You can try again any time.");
  }, [sp]);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout", { origin_url: origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg text-white">
      <Navbar />
      <div className="max-w-[900px] mx-auto px-6 md:px-10 pt-20 pb-24">
        <div className="text-xs uppercase tracking-[0.3em] text-brand-primary mb-4">Membership</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight">
          Unlock the <span className="text-brand-primary neon-text">whole network</span>
        </h1>
        <p className="text-brand-muted mt-5 max-w-xl">
          One subscription. Every original on BallOutTV — including <span className="text-white">Boyz Gone Wild</span>.
        </p>

        {subscribed ? (
          <div className="mt-14 border border-emerald-500/40 bg-emerald-500/10 p-8 flex items-start gap-4" data-testid="subscribe-active-banner">
            <CheckCircle2 className="h-7 w-7 text-emerald-400 shrink-0" />
            <div>
              <div className="font-display text-2xl uppercase">You're in.</div>
              <div className="text-emerald-200/80 text-sm mt-1">
                Active until {user.subscription_expires_at ? new Date(user.subscription_expires_at).toLocaleDateString() : "—"}.
              </div>
              <Button onClick={() => nav("/browse")} className="mt-5 bg-brand-primary hover:bg-brand-primaryHover rounded-none">
                Start watching
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-6">
            <div className="border border-white/10 bg-brand-surface p-8 md:p-10">
              <div className="text-xs uppercase tracking-[0.3em] text-brand-primary">BallOut Premium</div>
              <div className="flex items-end gap-2 mt-3">
                <div className="font-display text-7xl leading-none">$6.99</div>
                <div className="text-brand-muted pb-2">/ month</div>
              </div>
              <ul className="mt-7 space-y-3 text-sm" data-testid="subscribe-features-list">
                {[
                  "Full access to every episode",
                  "New drops every week",
                  "Cancel anytime",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-white/90">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={startCheckout}
                disabled={loading}
                data-testid="subscribe-checkout-btn"
                className="mt-8 w-full bg-brand-primary hover:bg-brand-primaryHover text-white rounded-none h-13 py-4 uppercase tracking-[0.15em] font-semibold"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Continue to Stripe · $6.99"}
              </Button>
              <button onClick={refresh} className="mt-3 text-[11px] uppercase tracking-[0.2em] text-brand-muted hover:text-white">
                Refresh status
              </button>
            </div>
            <div className="border border-white/10 bg-black/40 p-8 md:p-10 text-sm text-brand-muted">
              <div className="text-xs uppercase tracking-[0.3em] text-white mb-3 flex items-center gap-2"><Lock className="h-3 w-3" /> Secure checkout</div>
              <p>Your payment is processed by Stripe. Use test card <span className="text-white">4242 4242 4242 4242</span>, any future expiry, any CVC.</p>
              <p className="mt-4">Charges renew every 30 days. Manage or cancel any time from your account.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
